import Drawer from '@/components/Drawer';
import { FileUrlInput } from '@/components/FileLibrary';
import { F, getIntlTranslation } from '@/utils/intl';
import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  CloseOutlined,
  ExpandOutlined,
  MenuOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  ColorPicker,
  Input,
  InputNumber,
  Segmented,
  Select,
  Switch,
  Tooltip,
  Typography,
} from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import InputWithPartnerTranslation from '../../InputWithPartnerTranslation';
import { EMAIL_SAFE_FONTS, matchFontStack } from '../emailFonts';
import RichTextField from '../RichTextField';
import { richTextRejection } from '../richTextValue';
import { parseBinding } from '../translationBinding';
import TwigCodeEditor from '../TwigCodeEditor';
import { EmailVariableGroup } from '../types';
import { Block, Condition, ConditionOperator } from './document';
import {
  SPACING_SIDES,
  SpacingSide,
  editRunField,
  inspectorGroups,
  readColor,
  readSpacing,
  themeColorTokens,
  writeColorLiteral,
  writeColorToken,
  writeSpacing,
} from './inspector';
import { richTextRejectionMessage } from './issueMessages';
import { ManifestParam } from './manifest';
import { numberOptions, presetLabel } from './numberPresets';
import { Registry } from './registry';
import {
  CONDITION_OPERATORS,
  addClause,
  emptyCondition,
  operatorIsNumeric,
  operatorNeedsValue,
  removeClause,
  updateClause,
} from './visibility';

import styles from './InspectorPanel.less';

/**
 * Alignment values as the thing they describe. Keyed by the option string a component
 * declares, so a component using `justify` or a different set falls back to the dropdown
 * rather than rendering a blank.
 */
const ALIGN_ICONS: Record<string, React.ReactNode> = {
  left: <AlignLeftOutlined />,
  center: <AlignCenterOutlined />,
  right: <AlignRightOutlined />,
  justify: <MenuOutlined />,
};

/** True when every declared option has an icon — otherwise the dropdown is honest. */
function isAlignment(options?: string[]): boolean {
  return (
    !!options &&
    options.length > 1 &&
    options.every((option) => option in ALIGN_ICONS)
  );
}

/**
 * How each operator reads in the sentence "show this when …".
 *
 * Worded as a *test*, not as its Twig: `set` compiles to a bare truthiness check, which
 * "has a value" describes and `set` does not.
 */
const OPERATOR_LABELS: Record<
  ConditionOperator,
  { id: string; defaultMessage: string }
> = {
  set: {
    id: 'pages.emailCenter.visual.inspector.opSet',
    defaultMessage: 'has a value',
  },
  unset: {
    id: 'pages.emailCenter.visual.inspector.opUnset',
    defaultMessage: 'is empty',
  },
  eq: { id: 'pages.emailCenter.visual.inspector.opEq', defaultMessage: 'is' },
  neq: {
    id: 'pages.emailCenter.visual.inspector.opNeq',
    defaultMessage: 'is not',
  },
  contains: {
    id: 'pages.emailCenter.visual.inspector.opContains',
    defaultMessage: 'contains',
  },
  ncontains: {
    id: 'pages.emailCenter.visual.inspector.opNcontains',
    defaultMessage: 'does not contain',
  },
  // Spelled out rather than shown as `>` / `<=`: the row already reads as a sentence
  // ("bookings.count is more than 3"), and a bare glyph in a 116px select is a puzzle.
  gt: {
    id: 'pages.emailCenter.visual.inspector.opGt',
    defaultMessage: 'is more than',
  },
  gte: {
    id: 'pages.emailCenter.visual.inspector.opGte',
    defaultMessage: 'is at least',
  },
  lt: {
    id: 'pages.emailCenter.visual.inspector.opLt',
    defaultMessage: 'is less than',
  },
  lte: {
    id: 'pages.emailCenter.visual.inspector.opLte',
    defaultMessage: 'is at most',
  },
};

/** Sentinel for "not bound to the theme" in the colour binding select. */
const CUSTOM_COLOR = '__custom__';

interface InspectorPanelProps {
  block: Block | null;
  registry: Registry;
  labels?: Record<string, string>;
  /** From GET /email-variables — supplies the theme colour tokens. */
  variableGroups?: EmailVariableGroup[];
  /** Flat dot-path sample values; resolves a bound token to a real swatch. */
  previewContext?: Record<string, string>;
  disabled?: boolean;
  /**
   * `coalesce` names the *field* being edited: consecutive changes to the same
   * field are one undo step. Undefined means "its own step". The caller scopes it
   * to the block, since it is the caller that knows which block it dispatches to.
   */
  onChangeProps: (
    props: Record<string, unknown>,
    options?: { coalesce?: string },
  ) => void;
  /** Closes the current run, so the next keystroke starts a new undo step. */
  onEndEditRun: () => void;
  /** Cmd/Ctrl-click on an `{% include %}` inside a code field opens that block. */
  onOpenFragment?: (key: string) => void;
  /** Which language a translated field shows and edits by default. */
  language: string;
  /** The selected block's visibility condition, when it has one. */
  condition?: Condition | null;
  /**
   * Whether the rail's eye has the condition editor open. The editor is a
   * disclosure, not a mirror of `condition`: it stays open on a block that has no
   * clauses yet, which is how one gets typed in the first place.
   */
  conditionOpen?: boolean;
  /** Absent ⇒ the section is hidden. `null` clears the condition entirely. */
  onChangeVisibility?: (condition: Condition | null) => void;
  /** Names a minted translation key after the template (see `suggestKey`). */
  templateName?: string | null;
}

/**
 * The inspector: one control per param the selected component *declares*.
 *
 * Controls are wrapped in a `div`, not a `<label>`: a label swallows the click on a
 * `contenteditable` child, so focus never reached the code editor and the keystrokes
 * fell through to the page — where the global two-key navigation shortcut picked them
 * up and navigated away mid-edit. Observed in the browser.
 *
 * There are no per-component panels and no per-type special cases — the manifest
 * is the schema, so shipping a `.twig` file with a `{# @param #}` header is the
 * whole job of adding an editable property. The mapping from `ParamKind` to a
 * control is the only component-agnostic knowledge here; everything about which
 * controls exist, their labels, options and defaults comes from the component.
 *
 * Grouping and value conversion live in `inspector.ts` (pure, unit-tested); this
 * file is the widget layer.
 */
const InspectorPanel: React.FC<InspectorPanelProps> = ({
  block,
  registry,
  labels,
  variableGroups,
  previewContext,
  disabled,
  onChangeProps,
  onEndEditRun,
  onOpenFragment,
  language,
  templateName,
  condition,
  conditionOpen,
  onChangeVisibility,
}) => {
  const intl = useIntl();
  const definition = block ? registry.definitions[block.type] : undefined;

  const groups = useMemo(
    () =>
      definition ? inspectorGroups(definition, labels, block?.props ?? {}) : [],
    [definition, labels, block?.props],
  );

  const themeTokens = useMemo(
    () => themeColorTokens(variableGroups),
    [variableGroups],
  );

  /** Which `code` param, if any, is open in the full-height drawer. */
  const [expandedCode, setExpandedCode] = useState<string | null>(null);
  /** Which ramped number param has its exact-value field open. */
  const [customNumber, setCustomNumber] = useState<string | null>(null);

  const set = useCallback(
    (param: ManifestParam, value: unknown) => {
      onChangeProps({ [param.name]: value }, { coalesce: editRunField(param) });
    },
    [onChangeProps],
  );

  if (!block) {
    return (
      <div className={styles.inspector}>
        <span className={styles.hint}>
          <F
            id="pages.emailCenter.visual.inspector.empty"
            defaultMessage="Select a block to edit its properties."
          />
        </span>
      </div>
    );
  }

  if (!definition) {
    return (
      <div className={styles.inspector}>
        <span className={styles.hint}>
          <F
            id="pages.emailCenter.visual.inspector.unknownBlock"
            defaultMessage="This block’s component is not available, so its properties cannot be shown."
          />
        </span>
      </div>
    );
  }

  /** What the editor edits: the stored condition, or a blank one to start typing into. */
  const editing = condition ?? emptyCondition();

  /**
   * **When this block shows.**
   *
   * The model, the reducer action and the `{% if %}` emission all shipped in Sprint 0.6
   * and nothing ever reached them — a complete, tested, compiling feature that no user
   * could use. This is the missing control.
   *
   * Only the *editor* lives here, and only while the rail's eye has it open. Opening it
   * is a block action, so its button sits with the block's other actions on the rail
   * heading (`VisualEditorBody`) — conditions are rare, and a section that announced
   * itself on every block to say "no condition" was paying rent on the panel's best
   * space for a state nobody needed told.
   *
   * The eye is a *disclosure*, so the section renders on `conditionOpen` alone and falls
   * back to a blank clause when the block has none: closing the editor must never throw
   * away what is stored, and opening it on an unconditional block has to offer a clause
   * to fill. The blank shell is not written to the document — the author's first edit to
   * it is.
   */
  const visibilitySection =
    onChangeVisibility && conditionOpen ? (
      <div className={styles.visibility}>
        {/* Title and mode on one line: the mode is a word inside the heading's
            sentence ("shown when all of"), not a control of its own, and a
            full-width Segmented for a two-value choice was the loudest thing in
            the panel. */}
        <div className={styles.visibilityHeader}>
          <span className={styles.visibilityTitle}>
            <F
              id="pages.emailCenter.visual.inspector.visibility"
              defaultMessage="Shown when"
            />
          </span>
          {editing.clauses.length > 1 && (
            <Select
              size="small"
              variant="borderless"
              className={styles.visibilityMode}
              disabled={disabled}
              value={editing.mode}
              onChange={(next) =>
                onChangeVisibility({ ...editing, mode: next })
              }
              options={[
                {
                  value: 'all',
                  label: getIntlTranslation(intl, {
                    id: 'pages.emailCenter.visual.inspector.modeAll',
                    defaultMessage: 'all of',
                  }),
                },
                {
                  value: 'any',
                  label: getIntlTranslation(intl, {
                    id: 'pages.emailCenter.visual.inspector.modeAny',
                    defaultMessage: 'any of',
                  }),
                },
              ]}
            />
          )}
        </div>

        {editing.clauses.map((clause, index) => (
          // Index-keyed on purpose: a clause has no identity of its own, and the list
          // is only ever edited through the controls below.
          // eslint-disable-next-line react/no-array-index-key
          <div key={index} className={styles.clause}>
            <Select
              size="small"
              className={styles.clausePath}
              showSearch
              disabled={disabled}
              placeholder={getIntlTranslation(intl, {
                id: 'pages.emailCenter.visual.inspector.clausePath',
                defaultMessage: 'Pick a value…',
              })}
              value={clause.path || undefined}
              onChange={(next) =>
                onChangeVisibility(updateClause(editing, index, { path: next }))
              }
              options={(variableGroups ?? []).map((group) => ({
                label: group.namespace,
                options: group.items.map((item) => ({
                  value: `${group.namespace}.${item}`,
                  label: item,
                })),
              }))}
            />
            <div className={styles.clauseTest}>
              <Select
                size="small"
                className={styles.clauseOp}
                disabled={disabled}
                value={clause.op}
                onChange={(next) =>
                  onChangeVisibility(updateClause(editing, index, { op: next }))
                }
                options={CONDITION_OPERATORS.map((op) => ({
                  value: op,
                  label: getIntlTranslation(intl, OPERATOR_LABELS[op]),
                }))}
              />
              {operatorNeedsValue(clause.op) && (
                <Input
                  size="small"
                  disabled={disabled}
                  // `inputMode` rather than `type="number"`: the value is stored and
                  // compiled as text either way, and a number input would swallow a
                  // half-typed "-" and add spinners the rail has no room for.
                  inputMode={
                    operatorIsNumeric(clause.op) ? 'decimal' : undefined
                  }
                  value={clause.value ?? ''}
                  placeholder={getIntlTranslation(
                    intl,
                    operatorIsNumeric(clause.op)
                      ? {
                          id: 'pages.emailCenter.visual.inspector.clauseNumber',
                          defaultMessage: 'number',
                        }
                      : {
                          id: 'pages.emailCenter.visual.inspector.clauseValue',
                          defaultMessage: 'value',
                        },
                  )}
                  onChange={(event) =>
                    onChangeVisibility(
                      updateClause(editing, index, {
                        value: event.target.value,
                      }),
                    )
                  }
                />
              )}
              <Button
                size="small"
                type="text"
                className={styles.clauseRemove}
                disabled={disabled}
                icon={<CloseOutlined />}
                onClick={() => {
                  const next = removeClause(editing, index);
                  // Removing the last one means "always shown" — a block conditional on
                  // an empty list reads as hidden to whoever opens it next.
                  onChangeVisibility(next.clauses.length > 0 ? next : null);
                }}
              />
            </div>
          </div>
        ))}

        <Button
          size="small"
          type="text"
          className={styles.visibilityAdd}
          disabled={disabled}
          icon={<PlusOutlined />}
          onClick={() => onChangeVisibility(addClause(editing))}
        >
          <F
            id="pages.emailCenter.visual.inspector.addClause"
            defaultMessage="Add another"
          />
        </Button>
      </div>
    ) : null;

  const renderControl = (param: ManifestParam, label: string) => {
    const value = block.props?.[param.name];

    switch (param.kind) {
      case 'richtext':
        return renderRichText(
          param,
          label,
          typeof value === 'string' ? value : '',
        );

      case 'number':
        return renderNumber(param, value);

      case 'toggle':
        return (
          <Switch
            size="small"
            disabled={disabled}
            checked={value === true}
            onChange={(checked) => set(param, checked)}
          />
        );

      case 'select':
        // Alignment is a picture of itself; three words in a dropdown are three words to
        // read for something an icon states.
        if (isAlignment(param.options)) {
          return (
            <Segmented
              size="small"
              block
              className={styles.grow}
              disabled={disabled}
              value={typeof value === 'string' ? value : undefined}
              onChange={(next) => set(param, String(next))}
              options={(param.options ?? []).map((option) => ({
                value: option,
                label: (
                  <Tooltip title={option}>
                    <span className={styles.alignIcon}>
                      {ALIGN_ICONS[option]}
                    </span>
                  </Tooltip>
                ),
              }))}
            />
          );
        }

        return (
          <Select
            size="small"
            className={styles.grow}
            disabled={disabled}
            allowClear={!param.required}
            value={typeof value === 'string' ? value : undefined}
            onChange={(next) => set(param, next ?? undefined)}
            options={(param.options ?? []).map((option) => ({
              value: option,
              // Option values are Twig-level tokens (`sidebar_left`), not copy;
              // humanizing them here would misrepresent what gets emitted.
              label: option,
            }))}
          />
        );

      case 'code':
        /**
         * Arbitrary HTML *and* Twig — `native.html` holds whole legacy bodies. A
         * plain textarea would make that unreadable, and a WYSIWYG would rewrite
         * the markup: contenteditable normalisation applied to
         * `<!--{% if %}-->` is silent data loss.
         */
        return (
          <div className={styles.codeField}>
            <Tooltip
              title={
                <F
                  id="pages.emailCenter.visual.inspector.expandCode"
                  defaultMessage="Edit in a larger editor"
                />
              }
            >
              <Button
                size="small"
                type="text"
                className={styles.codeExpand}
                icon={<ExpandOutlined />}
                onClick={() => setExpandedCode(param.name)}
              />
            </Tooltip>
            <TwigCodeEditor
              value={typeof value === 'string' ? value : ''}
              readOnly={disabled}
              minHeight="200px"
              maxHeight="420px"
              variableGroups={variableGroups}
              onChange={(next) => set(param, next)}
              onOpenReference={onOpenFragment}
            />
            {/**
             * A second editor over the same prop, not a moved one.
             *
             * Unlike the rich-text field there is no shared instance to relocate —
             * `TwigCodeEditor` is controlled, so both read `value` and write through
             * the same `set()`. Whichever is on screen is authoritative and they
             * cannot disagree.
             */}
            {expandedCode === param.name && (
              <Drawer
                open
                width="calc(100vw - 200px)"
                title={label}
                onOpenChange={() => setExpandedCode(null)}
                onClose={() => setExpandedCode(null)}
              >
                <TwigCodeEditor
                  value={typeof value === 'string' ? value : ''}
                  readOnly={disabled}
                  minHeight="calc(100vh - 220px)"
                  maxHeight="calc(100vh - 220px)"
                  variableGroups={variableGroups}
                  onChange={(next) => set(param, next)}
                  onOpenReference={onOpenFragment}
                />
              </Drawer>
            )}
          </div>
        );

      case 'font':
        return renderFont(param, value);

      case 'css':
        return renderCss(param, value);

      case 'color':
        return renderColor(param, value);

      case 'spacing':
        return renderSpacing(param, value);

      case 'variable':
        return (
          <Select
            size="small"
            className={styles.grow}
            showSearch
            disabled={disabled}
            allowClear
            value={typeof value === 'string' ? value : undefined}
            onChange={(next) => set(param, next ?? undefined)}
            options={(variableGroups ?? []).map((group) => ({
              label: group.namespace,
              options: group.items.map((item) => ({
                value: `{{ ${group.namespace}.${item} }}`,
                label: `${group.namespace}.${item}`,
              })),
            }))}
          />
        );

      case 'text':
        return (
          <InputWithPartnerTranslation
            value={typeof value === 'string' ? value : ''}
            onChange={(next) => set(param, next)}
            language={language}
            templateName={templateName}
            fieldName={label}
            disabled={disabled}
            onEndEditRun={onEndEditRun}
          />
        );

      // Deliberately *not* translatable: a URL or an image is addressed, not worded.
      // Per-locale links are a real need, but they belong in the variable that
      // resolves them at send time, not in a copy catalog.
      /**
       * An image is a URL you can also pick or upload here.
       *
       * Both halves are kept: pasting a URL is right for an asset that already
       * lives somewhere (a CDN, the journey library), and the file library is
       * right for everything the partner owns — including the image they are
       * about to upload, which lands there as a file they can find again.
       */
      case 'image':
        return (
          <FileUrlInput
            size="small"
            disabled={disabled}
            value={typeof value === 'string' ? value : ''}
            onChange={(next) => set(param, next)}
            onBlur={onEndEditRun}
            kind="image"
            context="email_image"
          />
        );

      // Deliberately not translatable: a URL is addressed, not worded.
      case 'url':
      default:
        return (
          <Input
            size="small"
            disabled={disabled}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => set(param, event.target.value)}
            onBlur={onEndEditRun}
          />
        );
    }
  };

  /**
   * Rich text: WYSIWYG when the value can be represented exactly, the translation
   * field otherwise.
   *
   * The order of the three cases is the whole design:
   *
   * 1. **Bound to a translation** — the words are partner-wide and this field does not
   *    own them, so `InputWithPartnerTranslation` handles it and there is nothing to
   *    format. Checked *first*, or two controls would disagree about who owns the value.
   * 2. **Representable** — the formatting editor, with variables and translations as
   *    chips.
   * 3. **Not representable** — a partner's own markup, Twig logic, an HTML comment.
   *    The plain field, plus a sentence saying *why*, because a control that silently
   *    downgrades is indistinguishable from one that is broken.
   */
  function renderRichText(param: ManifestParam, label: string, value: string) {
    const plain = (
      <InputWithPartnerTranslation
        value={value}
        onChange={(next) => set(param, next)}
        language={language}
        templateName={templateName}
        fieldName={label}
        disabled={disabled}
        rows={3}
        // Leaving the field ends the undo run, so coming back later types into a new
        // step rather than extending the old one.
        onEndEditRun={onEndEditRun}
      />
    );

    if (parseBinding(value).kind === 'bound') return plain;

    const rejection = richTextRejection(value);
    if (rejection) {
      return (
        <>
          {plain}
          <span className={styles.hint}>
            {richTextRejectionMessage(rejection, intl)}
          </span>
        </>
      );
    }

    return (
      <RichTextField
        value={value}
        onChange={(next) => set(param, next)}
        language={language}
        variableGroups={variableGroups}
        disabled={disabled}
        onEndEditRun={onEndEditRun}
      />
    );
  }

  /**
   * Colour: a swatch plus a theme binding.
   *
   * Binding is a separate control from picking a hex on purpose — copying the
   * brand colour and *following* it are different intents, and only the second
   * keeps the email branded when the partner changes their theme.
   */
  function renderColor(param: ManifestParam, value: unknown) {
    const colour = readColor(value);
    const boundHex =
      colour.mode === 'token'
        ? previewContext?.[`theme.colors.${colour.token}`]
        : undefined;

    return (
      <div className={styles.colorRow}>
        <ColorPicker
          size="small"
          disabled={
            disabled || colour.mode === 'token' || colour.mode === 'expression'
          }
          allowClear
          value={colour.literal ?? boundHex ?? null}
          onChange={(_, hex) => set(param, writeColorLiteral(hex))}
          onClear={() => set(param, undefined)}
        />
        <Select
          size="small"
          className={styles.grow}
          disabled={disabled || themeTokens.length === 0}
          value={colour.mode === 'token' ? colour.token : CUSTOM_COLOR}
          onChange={(next) =>
            set(
              param,
              next === CUSTOM_COLOR
                ? writeColorLiteral(boundHex)
                : writeColorToken(next),
            )
          }
          options={[
            {
              value: CUSTOM_COLOR,
              label: (
                <F
                  id="pages.emailCenter.visual.inspector.customColor"
                  defaultMessage="Custom colour"
                />
              ) as unknown as string,
            },
            ...themeTokens.map((token) => ({
              value: token,
              label: `${token}`,
            })),
          ]}
        />
        {colour.mode === 'expression' && (
          <Tooltip title={colour.expression}>
            <Typography.Text type="warning" className={styles.expressionFlag}>
              <F
                id="pages.emailCenter.visual.inspector.colorExpression"
                defaultMessage="Twig"
              />
            </Typography.Text>
          </Tooltip>
        )}
      </div>
    );
  }

  /**
   * A numeric param as four named steps.
   *
   * Falls back to a number field only where no ramp makes sense (a column count, a
   * heading level) — the point is that "bigger or smaller" is the decision, and typing
   * `17` into a font size is not one worth offering.
   */
  function renderNumber(param: ManifestParam, value: unknown) {
    const options = numberOptions(param.name, value);
    if (!options) {
      return (
        <InputNumber
          size="small"
          className={styles.grow}
          disabled={disabled}
          value={typeof value === 'number' ? value : undefined}
          onChange={(next) => set(param, next ?? undefined)}
        />
      );
    }

    /**
     * The ramp, plus a way out of it.
     *
     * Four named steps are the right control for "bigger or smaller" and stay the
     * default. But a ramp is also a *ceiling*: an image width of 150px or a font
     * size of 17px was unreachable here, only editable in the Source view, because
     * the off-ramp option is shown for a value that already exists and cannot mint
     * a new one. The toggle reveals a number field on demand, so the panel keeps
     * one control per property and no value is out of reach.
     */
    const custom = customNumber === param.name;

    return (
      <div className={styles.numberField}>
        <Segmented
          size="small"
          block
          className={styles.grow}
          disabled={disabled}
          value={typeof value === 'number' ? value : undefined}
          onChange={(next) => set(param, Number(next))}
          options={options.map((preset) => ({
            value: preset.value,
            label: (
              <Tooltip title={`${preset.value}px`}>
                <span>{presetLabel(preset)}</span>
              </Tooltip>
            ),
          }))}
        />
        <Tooltip
          title={
            <F
              id="pages.emailCenter.visual.inspector.exactValue"
              defaultMessage="Enter an exact value"
            />
          }
        >
          <Button
            size="small"
            type="text"
            className={styles.numberExact}
            disabled={disabled}
            onClick={() => setCustomNumber(custom ? null : param.name)}
          >
            &hellip;
          </Button>
        </Tooltip>
        {custom && (
          <InputNumber
            autoFocus
            size="small"
            className={styles.numberInput}
            disabled={disabled}
            value={typeof value === 'number' ? value : undefined}
            onChange={(next) => set(param, next ?? undefined)}
            onBlur={onEndEditRun}
          />
        )}
      </div>
    );
  }

  /**
   * The font stack, from a list rather than a text box.
   *
   * Only families installed everywhere are offered (`EMAIL_SAFE_FONTS`) — an email
   * has no webfont to fall back on, so a family the recipient's client lacks is
   * silently swapped for one it has. The journey theme's own stack heads the list
   * as *Theme*, because matching the brand is the common case and it is the one
   * choice the component cannot know.
   *
   * Clearing it is meaningful, not empty: the component then resolves the theme
   * font itself, so "no value" is *inherit the theme*, which is what the placeholder
   * says.
   */
  function renderFont(param: ManifestParam, value: unknown) {
    const current = typeof value === 'string' ? value : '';
    const themeStack = previewContext?.font_family;

    const options = [
      ...(themeStack
        ? [
            {
              value: themeStack,
              label: getIntlTranslation(intl, {
                id: 'pages.emailCenter.visual.inspector.themeFont',
                defaultMessage: 'Theme font',
              }),
            },
          ]
        : []),
      ...EMAIL_SAFE_FONTS.filter((font) => font.stack !== themeStack).map(
        (font) => ({ value: font.stack, label: font.label }),
      ),
      // A stack set by hand, or one the theme used to carry, stays selectable
      // rather than reading as "inherit".
      ...(current && current !== themeStack && !matchFontStack(current)
        ? [{ value: current, label: current }]
        : []),
    ];

    return (
      <Select
        size="small"
        className={styles.grow}
        disabled={disabled}
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder={getIntlTranslation(intl, {
          id: 'pages.emailCenter.visual.inspector.inheritFont',
          defaultMessage: 'Inherit from the theme',
        })}
        value={current || undefined}
        onChange={(next) => set(param, next ?? undefined)}
        options={options}
      />
    );
  }

  /**
   * Inline CSS declarations, appended to whatever the component already declared.
   *
   * A textarea rather than the Twig code editor: this is a value inside a `style`
   * attribute, not markup, so syntax highlighting for tags and Twig tags would be
   * highlighting the wrong language. Monospaced and never spell-checked, because
   * every character is significant.
   */
  function renderCss(param: ManifestParam, value: unknown) {
    return (
      <Input.TextArea
        className={styles.cssInput}
        disabled={disabled}
        spellCheck={false}
        autoSize={{ minRows: 2, maxRows: 6 }}
        placeholder="letter-spacing:0.5px;text-transform:uppercase;"
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => set(param, event.target.value)}
        onBlur={onEndEditRun}
      />
    );
  }

  /**
   * Padding as one ramp rather than four boxes.
   *
   * It used to be T/R/B/L number fields — four text inputs for one visual decision, and
   * the densest thing in the panel. One ramp sets all four sides; the asymmetric case is
   * rare enough to belong in the Source view rather than in everyone's way. An existing
   * uneven value is shown as its own option, so it is never silently flattened.
   */
  function renderSpacing(param: ManifestParam, value: unknown) {
    const spacing = readSpacing(value);
    const sides = SPACING_SIDES.map((side: SpacingSide) => spacing[side]);
    const uniform = sides.every((each) => each === sides[0])
      ? sides[0]
      : undefined;
    const options = numberOptions('padding', uniform) ?? [];

    return (
      <Segmented
        size="small"
        block
        className={styles.grow}
        disabled={disabled}
        value={uniform}
        onChange={(next) =>
          set(
            param,
            SPACING_SIDES.reduce<unknown>(
              (carry, side) => writeSpacing(carry, side, Number(next)),
              value,
            ),
          )
        }
        options={
          uniform === undefined
            ? [
                ...options.map((preset) => ({
                  value: preset.value,
                  label: presetLabel(preset),
                })),
                // Says why nothing is selected, and is not selectable itself.
                {
                  value: -1,
                  disabled: true,
                  label: getIntlTranslation(intl, {
                    id: 'pages.emailCenter.visual.inspector.spacingUneven',
                    defaultMessage: 'Uneven',
                  }),
                },
              ]
            : options.map((preset) => ({
                value: preset.value,
                label: (
                  <Tooltip title={`${preset.value}px`}>
                    <span>{presetLabel(preset)}</span>
                  </Tooltip>
                ),
              }))
        }
      />
    );
  }

  /**
   * **There is no region control.** A top-level block's layout region is chosen by
   * dragging it into that band on the canvas — `adoptLayoutSlot` gives a moved block the
   * region of its new neighbours — so a dropdown repeating the same choice was a second
   * way to say the same thing, and the one that disagreed with what the canvas showed.
   */
  if (groups.length === 0) {
    return (
      <div className={styles.inspector}>
        {visibilitySection}
        <span className={styles.hint}>
          <F
            id="pages.emailCenter.visual.inspector.noParams"
            defaultMessage="This block has no editable properties."
          />
        </span>
      </div>
    );
  }

  return (
    <div className={styles.inspector}>
      {visibilitySection}
      {/* Inferred params are editable like any other — the note is about how much
          to trust their names and types, not about whether they work. */}
      {groups.map((group) => (
        <div key={group.id} className={styles.group}>
          <div className={styles.groupTitle}>
            {group.id === 'content' ? (
              <F
                id="pages.emailCenter.visual.inspector.content"
                defaultMessage="Content"
              />
            ) : (
              <F
                id="pages.emailCenter.visual.inspector.style"
                defaultMessage="Appearance"
              />
            )}
          </div>
          {group.controls.map(({ param, label }) => (
            <div
              key={param.name}
              className={`${styles.control} ${
                param.kind === 'toggle' ? styles.controlInline : ''
              }`}
            >
              <span className={styles.controlLabel}>
                {label}
                {param.required && <span className={styles.required}>*</span>}
              </span>
              {renderControl(param, label)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default React.memo(InspectorPanel);
